import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import PageSeo from '@/components/PageSeo';

export default function Privacy() {
  const ic = useInstallmentsCopy();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === '1';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-[70vh] bg-black text-white/80">
      <PageSeo path="/privacy" />
      <div className="mx-auto max-w-4xl px-6 py-12">
        {/* Back Button - hidden when embedded in overlay */}
        {!isEmbed && (
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        )}

        {/* Header */}
        <h1 className="text-3xl font-light text-white mb-2">Privacy Policy</h1>
        <p className="text-xs text-white/50 mb-12">
          Effective Date: October 24, 2026
        </p>

        {/* Privacy Policy Content */}
        <div className="space-y-8 text-[12.5px] leading-relaxed">
          
          {/* Section 1 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">1. Introduction</h2>
            <p>
              Welcome to Defera LLC ("we," "our," "us"). We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This Privacy Policy outlines how we collect, use, and protect your information when you visit our website. By using our website, you agree to the terms of this Privacy Policy.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">2. Information We Collect</h2>
            <p className="mb-3">We may collect various types of information, including:</p>
            
            <div className="space-y-3 ml-2">
              <div>
                <h3 className="text-sm font-normal text-white mb-2">2.1 Personal Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Contact Information: Your name, email address, phone number, postal address, and any other information you provide when you contact us or sign up for our services.</li>
                  <li>Account Information: If you create an account with us, we may collect information such as your username, password, and profile information.</li>
                  <li>Billing and Cardholder Information: The billing address, shipping address, and other contact address(es) associated with the payment method you use to book travel through Defera, together with the last four digits and card brand of the payment instrument (sensitive card data is collected and stored by our PCI-compliant payment processor, not by Defera).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">2.2 Usage Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Browsing Information: Information about how you use our website, including the pages you visit, the links you click, the time spent on each page, and other actions you take.</li>
                  <li>Interaction Information: Details of your interactions with our customer service team, including the date, time, and content of your communications.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">2.3 Technical Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Device Information: Information about the device you use to access our website, such as your IP address, browser type, operating system, device type, and unique device identifiers.</li>
                  <li>Log Information: Log data automatically collected when you access our website, including your IP address, browser type, operating system, the date and time of your visit, and the pages you viewed.</li>
                  <li>Precise Geolocation: Where available and permitted by your device, we collect your precise geolocation (including, where available, latitude/longitude or equivalent location signals derived from your IP address, Wi-Fi, GPS, or similar sources) at the time of booking and during each subsequent session. We use this information principally for fraud prevention, identity verification, and chargeback defense, as further described in Section 3.5 and Section 3.6.</li>
                  <li>Session and Authentication Metadata: Timestamps, session identifiers, device fingerprints, and similar metadata associated with each booking event and account session, retained for fraud-defense and dispute-response purposes.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">2.4 Travel Credit and Gift Card Information</h3>
                <p className="mb-2">When you choose to apply travel credits, certificates, vouchers, or gift cards to your booking, we collect:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Credit Instrument Details: The type of credit instrument (travel credit, certificate, voucher, or gift card), issuing airline, reference/confirmation numbers, PINs (if applicable), and claimed amounts.</li>
                  <li>Claimant Information: For travel credits, the last name of the original credit recipient to verify authorization.</li>
                  <li>Transaction Records: Records of credit applications, including timestamps, amounts applied, and booking associations.</li>
                </ul>
                <p className="mt-2 text-white/80">
                  This information is collected solely for the purpose of applying credits to your booking and is retained as part of your transaction records. Credit instrument information may be shared with airlines and payment processors as necessary to complete your booking.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">3. Use of Information</h2>
            <p className="mb-3">We use the information we collect for various purposes, including:</p>
            
            <div className="space-y-3 ml-2">
              <div>
                <h3 className="text-sm font-normal text-white mb-2">3.1 To Provide and Improve Our Services</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Service Delivery: To provide, operate, and maintain our website and services.</li>
                  <li>Service Enhancement: To improve our website, services, and user experience based on the feedback and usage patterns we observe.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">3.2 To Communicate with You</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Customer Support: To respond to your inquiries, questions, and requests.</li>
                  <li>Notifications: To send you important updates, such as changes to our policies, security alerts, and administrative messages.</li>
                  <li>Flight Alert Texts: If you opt in, to text you about the flights on your trips. We don&apos;t sell your mobile number or your text-message opt-in, and we don&apos;t share them with third parties or affiliates for their marketing or promotional purposes; only the service provider that sends the texts for us receives them. Reply STOP to any text to opt out.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">3.3 To Personalize Your Experience</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Customization: To tailor our website and services to better suit your preferences and interests.</li>
                  <li>Recommendations: To provide personalized recommendations based on your usage patterns.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">3.4 To Monitor and Analyze Usage and Trends</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Analytics: To conduct research and analysis on how our website is used, identifying trends, and evaluating the effectiveness of our marketing efforts.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">3.5 To Ensure Security and Compliance</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Security: To protect the security and integrity of our website and services, prevent fraudulent activity, and detect and investigate violations of our policies.</li>
                  <li>Fraud Prevention and Chargeback Defense: To prevent, detect, investigate, and defend against fraudulent transactions, "friendly fraud," and improper chargebacks, payment disputes, or reversals. For these purposes, we collect and process your IP address, precise geolocation, device fingerprint, billing and contact address(es), and session metadata, and we associate this information with each booking and each subsequent account session. Your acceptance of our Terms of Service authorizes this collection and processing; see Section 2.11 of the Terms of Service.</li>
                  <li>Identity Verification and Card-Authorization Validation: To verify that the person initiating a booking is the authorized cardholder and to validate that each payment is authorized by you.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">3.6 Disclosures for Fraud Prevention and Chargeback Defense</h3>
                <p className="mb-2">
                  In connection with the purposes described in Section 3.5, you authorize us to disclose, produce, and transmit the information described therein — including your IP address, precise geolocation, device fingerprint, billing and contact address(es), session metadata, transaction records, and any communications you have had with us — to the following recipients, to the extent reasonably necessary for the stated purpose:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Your card-issuing bank, card network (such as Visa, Mastercard, American Express, or Discover), and our acquiring bank;</li>
                  <li>Our payment processor and any chargeback-management or dispute-representment vendor we engage;</li>
                  <li>Third-party collection agencies to which a defaulted balance has been referred under Section 2.9 of the Terms of Service;</li>
                  <li>Consumer credit bureaus, to the extent permitted by applicable law and consistent with Section 2.9 of the Terms of Service;</li>
                  <li>Our legal counsel, auditors, and insurers; and</li>
                  <li>Regulatory or law-enforcement authorities, where required by law or where we reasonably believe disclosure is necessary to prevent, investigate, or prosecute suspected fraud.</li>
                </ul>
                <p className="mt-2">
                  Information disclosed under this Section is disclosed in connection with the defense of a legal claim and with your express consent as memorialized in the Terms of Service, and is not a "sale" or "sharing" of personal information for cross-context behavioral advertising purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">4. Advertising and Analytics Partners</h2>
            <p className="mb-3">
              We work with advertising partners, to measure how well our ads work (for example, whether an ad led to a sign-up or a booking), and with analytics partners, to understand how our website is used. These partners may collect information about your visit, such as the pages you view, your device and browser, and how you interact with our site, through cookies, pixels and similar technologies. To measure bookings, we may also send an advertising partner the booking amount together with a hashed (one-way scrambled) version of your email address.
            </p>
            <p>
              You can opt out of this sale or sharing of your personal information at any time on our{' '}
              <Link to="/privacy-choices" className="text-white/60 hover:text-white transition-colors underline">Do Not Sell or Share My Personal Information</Link>{' '}
              page. We also treat a Global Privacy Control signal from your browser as a valid request to opt out.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">5. Cookies and Tracking Technologies</h2>
            
            <div className="space-y-3 ml-2">
              <div>
                <h3 className="text-sm font-normal text-white mb-2">5.1 Cookies</h3>
                <p>
                  We use cookies and similar tracking technologies to track the activity on our website and hold certain information. Cookies are files with a small amount of data, which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">5.2 Types of Cookies We Use</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Essential Cookies: These cookies are necessary for the operation of our website and enable you to move around our website and use its features.</li>
                  <li>Performance Cookies: These cookies collect information about how you use our website, such as which pages you visit most often. This information helps us improve our website and services.</li>
                  <li>Functionality Cookies: These cookies allow our website to remember choices you make, such as your username, language, or the region you are in, and provide enhanced, more personalized features.</li>
                  <li>Targeting Cookies: These cookies record your visit to our website, the pages you have visited, and the links you have followed. We use this information to make our website and the advertising displayed on it more relevant to your interests.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">5.3 Managing Cookies</h3>
                <p>
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">6. Data Security</h2>
            <p className="mb-3">
              We take the security of your personal information seriously and use appropriate measures to protect it. These measures include:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Encryption: Using encryption to protect sensitive information transmitted online.</li>
              <li>Access Controls: Limiting access to personal information to authorized personnel only.</li>
              <li>Security Practices: Regularly reviewing our security practices to protect against unauthorized access, alteration, disclosure, or destruction of your personal information.</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission over the Internet or method of electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">7. Your Rights</h2>
            <p className="mb-3">You have the following rights regarding your personal data:</p>
            
            <div className="space-y-3 ml-2">
              <div>
                <h3 className="text-sm font-normal text-white mb-2">7.1 Access and Rectification</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Access: You have the right to access and receive a copy of the personal data we hold about you.</li>
                  <li>Rectification: You have the right to request the correction of any inaccurate personal data.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">7.2 Deletion and Objection</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Deletion: You have the right to request the deletion of your personal data under certain circumstances. You can delete your account yourself at any time in Profile &rarr; Delete account, on our website or in the Defera app. If nothing is still open on your account, it is deleted right away: we delete your profile, saved travelers, saved cards, linked cards, chats and preferences, and remove your login. If you have an active trip or payment plan, a Reserve plan, Defera Credit or a balance with our collections team, our team contacts you within 2 business days to settle it first, then deletes your account. We keep records of your trips and payments, without the personal details they don&rsquo;t need, for as long as the law requires, for example for accounting, tax, fraud prevention and disputes. Defera Miles on a deleted account are forfeited.</li>
                  <li>Objection: You have the right to object to the processing of your personal data for direct marketing purposes or if the processing is based on our legitimate interests.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-normal text-white mb-2">7.3 Restriction and Portability</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Restriction: You have the right to request the restriction of processing your personal data in certain circumstances.</li>
                  <li>Portability: You have the right to request the transfer of your personal data to another data controller in a structured, commonly used, and machine-readable format.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 7.4 */}
          <section>
            <h3 className="text-sm font-normal text-white mb-2">7.4 {ic.privacyPartnershipTitle}</h3>
            <p className="mb-3">{ic.privacy74Intro}</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                <strong className="text-white/90">Data Sharing:</strong> {ic.privacy74DataSharing}
              </li>
              <li>
                <strong className="text-white/90">{ic.partnerResponsibilitiesTitle}</strong> {ic.privacy74PartnerResponsibilities}
              </li>
              <li>
                <strong className="text-white/90">Third-Party Privacy Policy:</strong> {ic.privacy74ThirdPartyPrivacy}
              </li>
              <li>
                <strong className="text-white/90">Our Role:</strong> {ic.privacy74OurRole}
              </li>
            </ul>
            <p className="mt-3">{ic.privacy74ConsentClosing}</p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">8. Children's Privacy</h2>
            <p>
              Our website and services are not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have inadvertently received personal information from a user under the age of 13, we will delete such information from our records.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">9. International Data Transfers</h2>
            <p>
              We may transfer your personal information to, and process it in, countries other than the country in which you are resident. These countries may have data protection laws that are different from the laws of your country. However, we take measures to ensure that any such transfers comply with applicable data protection laws and that your personal information remains protected.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">10. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. The effective date of the revised Privacy Policy will be indicated at the top of the page. You are advised to review this Privacy Policy periodically for any changes. Your continued use of our website and services after the posting of any changes constitutes your acceptance of such changes.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">11. Contact Us</h2>
            <p className="mb-2">If you have any questions about this Privacy Policy, please contact us:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                Email: <a href="mailto:support@defera.app" className="text-white/60 hover:text-white transition-colors underline">support@defera.app</a>
              </li>
            </ul>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">12. Additional Information for California Residents</h2>
            <p className="mb-3">
              If you are a California resident, you have specific privacy rights under the California Consumer Privacy Act (CCPA). These rights include:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Right to Know: The right to request information about the personal data we have collected about you, the purposes for which we use it, and the types of third parties with whom we share it.</li>
              <li>Right to Delete: The right to request the deletion of your personal data, subject to certain exceptions.</li>
              <li>Right to Opt Out of Sale or Sharing: The right to opt out of the sale or sharing of your personal information, which you can do on our <Link to="/privacy-choices" className="text-white/60 hover:text-white transition-colors underline">Do Not Sell or Share My Personal Information</Link> page or with a Global Privacy Control signal from your browser.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@defera.app" className="text-white/60 hover:text-white transition-colors underline">support@defera.app</a>. We will verify your identity before processing your request.
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-sm font-normal text-white mb-3">13. Our AI Concierge</h2>
            <p>
              The Defera Concierge, in chat and on the phone, is an AI concierge. When you use it, your messages, and the details of your trips it needs to answer you, are processed by our AI service providers to generate its replies. Chats you have while signed in are saved to your account, and our team, including AI quality reviewers, reviews conversations to keep answers accurate and to improve our service. The concierge never needs your password, card number or government ID numbers, so please don't share them with it. Our{' '}
              <Link to="/security/ai-and-your-data" className="text-white/60 hover:text-white transition-colors underline">AI and your data</Link>{' '}
              page explains more.
            </p>
          </section>

          {/* Footer */}
          <section className="border-t border-white/10 pt-8 mt-12">
            <p className="text-white/50">Last updated: October 24, 2026</p>
          </section>
        </div>
      </div>
    </div>
  );
}
